/** Cliente HTTP del PWA: sesion en cookies + CSRF (mismo contrato que la web). */
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';
const CSRF_COOKIE = 'erpa_csrf';

function csrfToken(): string | null {
  const entry = document.cookie.split('; ').find((part) => part.startsWith(`${CSRF_COOKIE}=`));
  return entry ? decodeURIComponent(entry.slice(CSRF_COOKIE.length + 1)) : null;
}

function addCsrf(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const method = config.method?.toUpperCase() ?? 'GET';
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = csrfToken();
    if (token) config.headers.set('X-CSRF-Token', token);
  }
  return config;
}

export const mobileApiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

mobileApiClient.interceptors.request.use(addCsrf);

let refreshPromise: Promise<void> | null = null;

function isMobileProtectedPath(pathname: string): boolean {
  return pathname.startsWith('/m') && pathname !== '/m/login';
}

mobileApiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const path = request?.url ?? '';
    const isAuthAction = /\/api\/v1\/auth\/(login|refresh|logout|forgot-password|reset-password)$/.test(
      path,
    );
    if (error.response?.status !== 401 || !request || request._retry || isAuthAction) {
      return Promise.reject(error);
    }

    request._retry = true;
    try {
      refreshPromise ??= mobileApiClient
        .post('/api/v1/auth/refresh')
        .then(() => undefined)
        .finally(() => {
          refreshPromise = null;
        });
      await refreshPromise;
      return mobileApiClient(request);
    } catch (refreshError) {
      // En v1 el PWA no expulsa por inactividad: la sesion vive mientras el
      // refresh de 7 dias rote. Solo se redirige al login en rutas protegidas.
      if (isMobileProtectedPath(window.location.pathname)) {
        window.location.href = '/m/login';
      }
      return Promise.reject(refreshError);
    }
  },
);

export function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail.replace(/^[A-Z][A-Z_]*:\s*/, '');
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Error desconocido';
}

export function extractRevisionConflict(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 409) return false;
  const code = error.response?.data?.code;
  if (typeof code === 'string') return code === 'REVISION_CONFLICT';
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' && detail.startsWith('REVISION_CONFLICT');
}

/** Clave de idempotencia por intencion (una por intento del operador). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
